import type { StaffRole } from "@lafayette/auth";
import { useMemo } from "react";

import { type CapabilityRules, capabilityFlags } from "../access/capabilities";
import { useStaffRoles } from "../access/use-staff-roles";

// What a staff member may do in the Payments UI. Presentation only: the API decides every request again (a concierge also needs the
// relationship to the customer, and the API answers 403 without telling which). The roles come from the domain's `capabilityRoles`.

export type PaymentCapabilities = Readonly<{
  /** `payment.read_console`: the Payments list and detail. */
  read: boolean;
  /** `payment.read`: customer data, so the quote panel of a request. */
  readQuotes: boolean;
  /** `payment.reconcile` */
  reconcile: boolean;
  /** `payment.refund` */
  refund: boolean;
  /** `payment.record_quote` */
  recordQuote: boolean;
  /** `payment.issue_quote_request` */
  issueQuoteRequest: boolean;
}>;

const paymentCapabilityRules = {
  read: "payment.read_console",
  readQuotes: "payment.read",
  reconcile: "payment.reconcile",
  refund: "payment.refund",
  recordQuote: "payment.record_quote",
  issueQuoteRequest: "payment.issue_quote_request",
} as const satisfies CapabilityRules;

export function paymentCapabilitiesForRoles(roles: readonly StaffRole[]): PaymentCapabilities {
  return capabilityFlags(paymentCapabilityRules, roles);
}

export function usePaymentCapabilities(): Readonly<{
  capabilities: PaymentCapabilities;
  isLoading: boolean;
}> {
  const { roles, isLoading } = useStaffRoles();
  const capabilities = useMemo(() => paymentCapabilitiesForRoles(roles), [roles]);
  return { capabilities, isLoading };
}
